import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'

import { NotyAnnouncementsUI, NotyRendererUI } from './NotyRenderer.css'

import { ItemShape, UNDO_HOTKEY, isAlertNoty } from '../../Noty.constants'
import NotyElement from '../NotyElement'

// A live region skips content that arrives together with it, so a toast shown on page load waits.
export const ANNOUNCE_DELAY = 100
// Long enough to be read out, short enough that reading the page does not meet the message twice.
export const ANNOUNCEMENT_DURATION = 5000
// Sighted users see the shortcut on the Undo button, which the announcement leaves out.
export const UNDO_HINT = `. Press ${UNDO_HOTKEY.toUpperCase()} to undo.`

function Announcement({ children, regionsMountedAt }) {
  const [delay] = useState(() =>
    Math.max(0, regionsMountedAt + ANNOUNCE_DELAY - Date.now())
  )
  const [isWaiting, setIsWaiting] = useState(delay > 0)
  const [isDone, setIsDone] = useState(false)

  useEffect(() => {
    const timer = isWaiting
      ? setTimeout(() => setIsWaiting(false), delay)
      : setTimeout(() => setIsDone(true), ANNOUNCEMENT_DURATION)
    return () => clearTimeout(timer)
  }, [delay, isWaiting])

  if (isDone) return null
  return <div>{isWaiting ? null : children}</div>
}

Announcement.propTypes = {
  children: PropTypes.node,
  regionsMountedAt: PropTypes.number.isRequired,
}

/**
 * @typedef {import('../../Noty.constants').TNotyItem} TNotyItem
 * @param {{notys: Array.<TNotyItem>}}
 */
function NotyRenderer({ notys }) {
  const [regionsMountedAt] = useState(Date.now)

  // notys is newest first, so one press of the hotkey undoes the latest action only.
  const undoHotkeyId = notys.find(
    item => item.opts?.dangerButtonProps?.onClick
  )?.id

  const alertNotys = notys.filter(isAlertNoty)
  const politeNotys = notys.filter(item => !isAlertNoty(item))

  const renderAnnouncements = items =>
    items.map(item => (
      <Announcement key={item.id} regionsMountedAt={regionsMountedAt}>
        {item.content}
        {item.id === undoHotkeyId && UNDO_HINT}
      </Announcement>
    ))

  return (
    <>
      <NotyRendererUI
        role="region"
        aria-label="Notifications"
        data-testid="NotyRenderer"
      >
        {[...alertNotys, ...politeNotys].map(item => (
          <NotyElement
            key={item.id}
            {...item}
            hasUndoHotkey={item.id === undoHotkeyId}
          />
        ))}
      </NotyRendererUI>
      {/* The live regions get only the message, so the buttons are not announced with it.
          No role="alert": an empty alert would match every getByRole('alert') in app tests. */}
      <NotyAnnouncementsUI>
        <div aria-live="assertive" aria-atomic="false" data-testid="NotyAlerts">
          {renderAnnouncements(alertNotys)}
        </div>
        <div role="status" aria-atomic="false">
          {renderAnnouncements(politeNotys)}
        </div>
      </NotyAnnouncementsUI>
    </>
  )
}

NotyRenderer.propTypes = {
  notys: PropTypes.arrayOf(PropTypes.shape(ItemShape)),
}

export default NotyRenderer
